import { shrpaApi } from "./axios";

// Types
import {
  SocialShareSummaryPayload,
  SocialMediaPostData,
  SocialPostCreatePayload,
  SocialPostsStructured,
  ImageSelectAIDebugData,
  SocialPostGenResultPayload,
  UpdatePostScheduledAtPayload,
  UpdatePostScheduledAtResponse,
} from "@contracts/shareStop";

export default {
  async loadSocialShareSummaryPayload(pageId: string, postType: string, contextId: string): Promise<SocialShareSummaryPayload> {
    const query = `?${postType}Id=${contextId}`;
    const uri = `/social/v2/${pageId}/summary-details${query}`;
    const { data } = await shrpaApi.get(uri);

    return data;
  },

  async loadSocialShareSummaryPayloadFromPostId(customerId: string, postId: string): Promise<SocialShareSummaryPayload> {
    const uri = `/social/v2/${customerId}/summary-details/${postId}`;
    const { data } = await shrpaApi.get<SocialShareSummaryPayload>(uri);

    return data;
  },

  async loadSocialShareSummaryPayloadFromCalendarItemId(customerId: string, calendarItemId: string): Promise<SocialShareSummaryPayload> {
    const uri = `/social/v2/${customerId}/summary-details/calendar-item/${calendarItemId}`;
    const { data } = await shrpaApi.get<SocialShareSummaryPayload>(uri);

    return data;
  },

  async loadSocialShareSummaryPayloadFromSummaryId(customerId: string, contentSummaryId: string): Promise<SocialShareSummaryPayload> {
    const uri = `/social/v2/${customerId}/summary-details/summary/${contentSummaryId}`;
    const { data } = await shrpaApi.get<SocialShareSummaryPayload>(uri);

    return data;
  },

  async loadAllPostsStructured(pageId: string): Promise<SocialPostsStructured> {
    const uri = `/social/v2/${pageId}/posts/structured`;
    const { data } = await shrpaApi.get<SocialPostsStructured>(uri);

    return data;
  },

  async loadLatestWeeklyPosts(pageId: string): Promise<Array<SocialMediaPostData>> {
    const uri = `/social/v2/${pageId}/posts/last-weekly-run`;
    const { data } = await shrpaApi.get<Array<SocialMediaPostData>>(uri);

    return data;
  },

  async loadAllPosts(pageId: string): Promise<Array<SocialMediaPostData>> {
    const uri = `/social/v2/${pageId}/posts`;
    const { data } = await shrpaApi.get<Array<SocialMediaPostData>>(uri);

    return data;
  },

  async deletePost(pageId: string, postId: string, reasonText: string) {
    const config = { headers: { "Content-Type": "application/json" } };
    const uri = `/social/v2/${pageId}/posts/${postId}`;
    await shrpaApi.delete(uri, { data: JSON.stringify(reasonText), ...config });
  },

  async savePost(pageId: string, payload: SocialPostCreatePayload): Promise<SocialMediaPostData> {
    const uri = `/social/v2/${pageId}/posts`;
    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.post<SocialMediaPostData>(uri, JSON.stringify(payload), config);

    return data;
  },

  async requestDownloadId(customerId: string, postId: string): Promise<string> {
    const uri = `/social/v2/${customerId}/posts/${postId}/download`;
    const { data } = await shrpaApi.get(uri);

    return data;
  },

  async updatePost(postData: SocialMediaPostData): Promise<SocialMediaPostData> {
    const uri = `/social/v2/${postData.customerId}/posts/${postData.id}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put(uri, JSON.stringify(postData), config);

    return data;
  },

  async updatePostScheduledAt(postData: SocialMediaPostData, localDateTime: string, timeZoneId: string): Promise<UpdatePostScheduledAtResponse> {
    const uri = `/social/v2/${postData.customerId}/posts/${postData.id}/scheduled`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put(uri, JSON.stringify({ localDateTime, timeZoneId } as UpdatePostScheduledAtPayload), config);

    return data;
  },

  async getDebugInfo(customerId: string): Promise<Array<ImageSelectAIDebugData>> {
    const uri = `/social-generator/${customerId}/debug`;
    const { data } = await shrpaApi.get<Array<ImageSelectAIDebugData>>(uri);
    return data;
  },

  async rerunEvalsForPost(customerId: string, postId: string): Promise<string> {
    const uri = `/evals/${customerId}/posts/${postId}/re-run`;
    const { data } = await shrpaApi.put<string>(uri);
    return data;
  },

  async genSocialPosts(customerId: string, assetIds: Array<string>, userGuidance: string, postCount = 2, saveResults = true, isDashboardPostCreate = false): Promise<SocialPostGenResultPayload> {
    const url = `social-generator/${customerId}/selected-assets/v2`;

    const payload = JSON.stringify({
      assetIds,
      userGuidance,
      postCount,
      saveResults,
      isDashboardPostCreate,
    });

    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.post<SocialPostGenResultPayload>(url, payload, config);

    return data;
  },

  async upsertPostUsageLink(customerId: string, postId: string, postUrl: string): Promise<SocialMediaPostData> {
    const uri = `/social/v2/${customerId}/posts/${postId}/post-url`;
    const payload = JSON.stringify(postUrl);
    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.put<SocialMediaPostData>(uri, payload, config);

    return data;
  },
};
